import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const Quiz = ({ questions, onSubmit }) => {
    const [userAnswers, setUserAnswers] = useState({});
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [score, setScore] = useState(null);
    const { theme } = useTheme();

    if (!questions || !Array.isArray(questions)) {
        return null;
    }

    const resetQuiz = () => {
        setUserAnswers({});
        setQuizSubmitted(false);
        setScore(null);
    };

    const getOptionClassName = (question, option, index) => {
        if (quizSubmitted) {
            if (option === question.correct_answer) {
                return 'bg-green-100 text-green-800 border-green-200';
            }
            if (userAnswers[index] === option) {
                return 'bg-red-100 text-red-800 border-red-200';
            }
            return 'bg-secondary text-accent';
        }
        return userAnswers[index] === option
            ? 'bg-primary/20 border-primary'
            : 'hover:bg-secondary text-accent';
    };

    const handleAnswerSelect = (questionIndex, answer) => {
        if (quizSubmitted) return;
        setUserAnswers(prev => ({
            ...prev,
            [questionIndex]: answer
        }));
    };

    const handleShortAnswer = (questionIndex, answer) => {
        if (quizSubmitted) return;
        setUserAnswers(prev => ({
            ...prev,
            [questionIndex]: answer
        }));
    };

    const submitQuiz = () => {
        if (!questions) return;
        
        let correctAnswers = 0;
        questions.forEach((question, index) => {
            if (question.type === 'multiple_choice') {
                if (userAnswers[index] === question.correct_answer) {
                    correctAnswers++;
                }
            } else if (question.type === 'short_answer') {
                const userAnswer = userAnswers[index]?.toLowerCase() || '';
                const hasMatchingKeywords = question.keywords.some(keyword => 
                    userAnswer.includes(keyword.toLowerCase())
                );
                if (hasMatchingKeywords) {
                    correctAnswers++;
                }
            }
        });

        const finalScore = Math.round((correctAnswers / questions.length) * 100);
        setScore(finalScore);
        setQuizSubmitted(true);
        onSubmit?.(finalScore);
    };

    const renderMultipleChoice = (question, index) => (
        <div className="space-y-2">
            {question.options.map((option, optionIndex) => (
                <button
                    key={optionIndex}
                    onClick={() => handleAnswerSelect(index, option)}
                    disabled={quizSubmitted}
                    className={`
                        w-full text-left p-4 rounded-lg transition-all border border-theme
                        ${getOptionClassName(question, option, index)}
                    `}
                >
                    {option}
                </button>
            ))}
        </div>
    );

    const renderShortAnswer = (question, index) => (
        <div className="space-y-2">
            <textarea
                value={userAnswers[index] || ''}
                onChange={(e) => handleShortAnswer(index, e.target.value)}
                disabled={quizSubmitted}
                className={`
                    w-full p-4 rounded-lg transition-all border border-theme
                    bg-secondary text-theme
                    ${quizSubmitted ? 'opacity-50' : ''}
                `}
                rows={4}
                placeholder="Type your answer here..."
            />
        </div>
    );

    const renderFeedback = (question, index) => {
        if (!quizSubmitted) return null;
        
        let isCorrect = false;
        if (question.type === 'multiple_choice') {
            isCorrect = userAnswers[index] === question.correct_answer;
        } else if (question.type === 'short_answer') {
            const userAnswer = userAnswers[index]?.toLowerCase() || '';
            isCorrect = question.keywords.some(keyword => 
                userAnswer.includes(keyword.toLowerCase())
            );
        }
        
        const feedbackClass = isCorrect
            ? 'bg-green-50 text-green-700'
            : 'bg-red-50 text-red-700';
        
        return (
            <div className={`mt-4 p-4 rounded-lg ${feedbackClass}`}>
                <div className="flex items-center gap-2">
                    {isCorrect ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    )}
                    <p className="font-medium">
                        {isCorrect ? 'Correct!' : 'Incorrect'}
                    </p>
                </div>
                {question.type === 'multiple_choice' ? (
                    <p className="text-sm mt-1">{question.explanation}</p>
                ) : (
                    <p className="text-sm mt-1">Sample Answer: {question.sample_answer}</p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {questions.map((question, index) => (
                <div key={index} className="p-6 rounded-xl bg-theme border border-theme theme-transition">
                    <h3 className="text-lg font-semibold mb-4 text-theme">
                        {index + 1}. {question.question}
                    </h3>
                    {question.type === 'multiple_choice' 
                        ? renderMultipleChoice(question, index)
                        : renderShortAnswer(question, index)
                    }
                    {renderFeedback(question, index)}
                </div>
            ))}
            
            <div className="flex justify-center gap-4">
                {!quizSubmitted && Object.keys(userAnswers).length === questions.length && (
                    <button
                        onClick={submitQuiz}
                        className="px-6 py-3 bg-primary text-theme rounded-lg hover:bg-primary-hover transition-colors"
                    >
                        Submit Quiz
                    </button>
                )}

                {quizSubmitted && (
                    <button
                        onClick={resetQuiz}
                        className="px-6 py-3 bg-secondary text-theme rounded-lg hover:bg-primary-hover transition-colors"
                    >
                        Retake Quiz
                    </button>
                )}
            </div>

            {quizSubmitted && (
                <div className={`text-center p-6 rounded-xl border ${
                    score >= 70 
                        ? 'bg-green-50 border-green-200 text-green-800' 
                        : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                }`}>
                    <h3 className="text-2xl font-bold mb-2">Your Score: {score}%</h3>
                    <p className="mb-4">{score >= 70 ? 'Great job!' : 'Keep practicing!'}</p>
                    <p className="text-sm text-accent">
                        {score >= 70 
                            ? 'Feel free to retake the quiz to reinforce your understanding!' 
                            : 'Try again to improve your score!'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default Quiz; 